import React, { useState } from 'react';
import {
  Search,
  Plus,
  Clock,
  Calendar,
  Trash2,
  MessageSquare,
  CheckCircle,
  XCircle,
  Pause,
  X
} from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { useListScheduledMessagesQuery, ScheduledMessageItem } from '@/client/api/whatsapp/list-scheduled-messages.query';
import { useCreateScheduledMessageMutation } from '@/client/api/whatsapp/create-scheduled-message.mutation';
import { useCancelScheduledMessageMutation } from '@/client/api/whatsapp/cancel-scheduled-message.mutation';
import ConfirmDeleteDialog from '@/client/components/admin/WhatsApp/ConfirmDeleteDialog';

const statusConfig: Record<string, { label: string; style: string; icon: React.ReactNode }> = {
  pending: { label: 'Pending', style: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: <Clock className="w-3 h-3" /> },
  sent: { label: 'Sent', style: 'bg-green-100 text-green-700 border-green-200', icon: <CheckCircle className="w-3 h-3" /> },
  canceled: { label: 'Canceled', style: 'bg-gray-100 text-gray-700 border-gray-200', icon: <Pause className="w-3 h-3" /> },
  failed: { label: 'Failed', style: 'bg-red-100 text-red-700 border-red-200', icon: <XCircle className="w-3 h-3" /> }
};

const previewFor = (msg: ScheduledMessageItem) =>
  msg.payload?.body || (msg.payload?.template_name ? `Template: ${msg.payload.template_name}` : `[${msg.message_type}]`);

const ScheduledMessagesPage: React.FC = () => {
  const { data, isLoading, refetch } = useListScheduledMessagesQuery();
  const messages = data?.data?.scheduled_messages || [];
  const createMutation = useCreateScheduledMessageMutation();
  const cancelMutation = useCancelScheduledMessageMutation();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [to, setTo] = useState('');
  const [body, setBody] = useState('');
  const [sendAt, setSendAt] = useState('');
  const [cancelTarget, setCancelTarget] = useState<ScheduledMessageItem | null>(null);

  const filtered = messages.filter(m => {
    const matchesSearch = m.to.includes(searchQuery);
    const matchesStatus = statusFilter === 'all' || m.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleCreate = () => {
    if (!to.trim() || !body.trim() || !sendAt) return;
    const sendAtUnix = Math.floor(new Date(sendAt).getTime() / 1000);
    if (sendAtUnix <= Math.floor(Date.now() / 1000)) {
      toast.error('Scheduled time must be in the future');
      return;
    }
    createMutation.mutate(
      { to, message_type: 'text', payload: { body }, send_at: sendAtUnix },
      {
        onSuccess: () => {
          toast.success('Message scheduled');
          setShowModal(false);
          setTo(''); setBody(''); setSendAt('');
          refetch();
        },
        onError: (error) => toast.error(error.message || 'Failed to schedule the message'),
      }
    );
  };

  const confirmCancel = () => {
    if (!cancelTarget) return;
    cancelMutation.mutate(
      { schedule_id: cancelTarget.schedule_id },
      {
        onSuccess: () => { toast.success('Scheduled message canceled'); setCancelTarget(null); refetch(); },
      }
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Scheduled Messages</h1>
            <p className="text-sm text-gray-500 mt-1">Schedule and manage future WhatsApp messages</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Schedule Message
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Total Scheduled</span>
              <Calendar className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{messages.length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Pending</span>
              <Clock className="w-5 h-5 text-yellow-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {messages.filter(m => m.status === 'pending').length}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Sent</span>
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {messages.filter(m => m.status === 'sent').length}
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by phone..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="sent">Sent</option>
                <option value="canceled">Canceled</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scheduled At</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading && (
                  <tr><td colSpan={5} className="px-6 py-4 text-sm text-gray-500 text-center">Loading...</td></tr>
                )}
                {filtered.map(msg => {
                  const cfg = statusConfig[msg.status];
                  return (
                    <tr key={msg.schedule_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{msg.to}</td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-600 line-clamp-2 max-w-xs">{previewFor(msg)}</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="flex items-center gap-1 text-sm text-gray-900">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {moment.unix(msg.send_at).format('YYYY-MM-DD HH:mm')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg?.style}`}>
                          {cfg?.icon}
                          {cfg?.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {msg.status === 'pending' && (
                          <button
                            onClick={() => setCancelTarget(msg)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                            title="Cancel"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!isLoading && filtered.length === 0 && (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No scheduled messages found</h3>
              <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Schedule a Message</h2>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Phone</label>
                <input
                  type="tel"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="+1234567890"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Send At</label>
                <input
                  type="datetime-local"
                  value={sendAt}
                  onChange={(e) => setSendAt(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <p className="text-xs text-gray-500">Only plain text messages can be scheduled here. Use Broadcasts for template campaigns.</p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100">
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending || !to.trim() || !body.trim() || !sendAt}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
              >
                {createMutation.isPending ? 'Scheduling...' : 'Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDeleteDialog
        open={!!cancelTarget}
        onOpenChange={(open) => !open && setCancelTarget(null)}
        title="Cancel Scheduled Message"
        itemLabel={cancelTarget?.to || 'this message'}
        description="The message will not be sent. This action cannot be reversed."
        confirmLabel="Cancel Message"
        pendingLabel="Canceling..."
        onConfirm={confirmCancel}
        isPending={cancelMutation.isPending}
        errorMessage={cancelMutation.error?.message}
      />
    </div>
  );
};

export default ScheduledMessagesPage;
